/**
 * Public family portal invite page: /portal/accept?code=... (Phase 12, plan §3.2 / §4.1).
 *
 * 1. Guardian enters the patient's last name and date of birth (code comes from the link).
 *    A Turnstile check is added once the API answers TURNSTILE_REQUIRED.
 * 2. Sets a password (skipped when the email already has a guardian account).
 * 3. Signs in through the normal login proxy and lands on "Awaiting approval".
 *
 * Errors never say which field was wrong. SECURITY: nothing typed here is logged.
 */
import React, { useEffect, useId, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader } from 'lucide-react';
import PortalService, { INVITE_MESSAGES, PortalInviteError } from '../../../services/portal/portal.service';
import type { VerifyInviteResponse } from '../../../dtos/portal/portalInvite.dto';
import TurnstileWidget from '../../auth/TurnstileWidget';
import { PASSWORD_RULES, isPasswordValid } from '../../../utils/passwordPolicy';
import { AuthProxyService, hasTokens } from '../../../services/auth/authProxy.service';
import { saveSession, getUserAttributesFromToken } from '../../../services/auth/cognito.service';
import { useAuth } from '../../../context/authcontext';
import { FOCUS_RING } from '../format';

/** State the login page reads to prefill the email and show a notice. */
export interface LoginPrefillState {
  prefillEmail?: string;
  notice?: string;
}

type Step = 'details' | 'password' | 'signing-in' | 'blocked';

interface PortalAcceptPageProps {
  /** App's sign-in callback (switches to the signed-in route tree). */
  onSignedIn?: () => void;
}

const inputClass = `mt-1 block w-full min-h-11 rounded-md border border-slate-400 bg-white px-3 text-base text-slate-900 ${FOCUS_RING}`;
const buttonClass = `inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-slate-800 px-4 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 ${FOCUS_RING}`;

const PortalAcceptPage: React.FC<PortalAcceptPageProps> = ({ onSignedIn }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const code = (params.get('code') || '').trim();
  const ids = useId();

  const [step, setStep] = useState<Step>(code ? 'details' : 'blocked');
  const [blockedMessage, setBlockedMessage] = useState<string>(
    code ? '' : 'This invitation link is incomplete. Open the link from your invitation email again.'
  );
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [turnstileRequired, setTurnstileRequired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [verified, setVerified] = useState<VerifyInviteResponse | null>(null);

  useEffect(() => {
    document.title = 'Accept invitation · pelora';
  }, []);

  const goToLogin = (email: string, notice: string) => {
    const state: LoginPrefillState = { prefillEmail: email, notice };
    navigate('/login', { replace: true, state });
  };

  const handleInviteError = (err: unknown) => {
    const inviteErr = err instanceof PortalInviteError ? err : new PortalInviteError('UNKNOWN');
    if (inviteErr.code === 'TURNSTILE_REQUIRED') setTurnstileRequired(true);
    if (inviteErr.code === 'INVITE_EXPIRED' || inviteErr.code === 'INVITE_LOCKED' || inviteErr.code === 'PORTAL_UNAVAILABLE') {
      setBlockedMessage(inviteErr.message);
      setStep('blocked');
      return;
    }
    if (inviteErr.code === 'REGISTRATION_TOKEN_INVALID') {
      setVerified(null);
      setPassword('');
      setConfirm('');
      setStep('details');
    }
    setError(inviteErr.message);
  };

  /** Register (password optional when the account exists), then sign in. */
  const register = async (verifiedInvite: VerifyInviteResponse, newPassword?: string) => {
    const result = await PortalService.registerInvite({
      registrationToken: verifiedInvite.registrationToken,
      password: newPassword,
    });
    if (!newPassword) {
      goToLogin(result.email, 'This patient has been linked to your account. Sign in with your existing password.');
      return;
    }
    setStep('signing-in');
    try {
      const login = await AuthProxyService.login(result.email, newPassword);
      if (!hasTokens(login)) {
        // MFA or another challenge: finish on the normal sign-in page
        goToLogin(result.email, 'Your account is ready. Sign in to continue.');
        return;
      }
      saveSession({
        idToken: login.tokens.idToken,
        accessToken: login.tokens.accessToken,
        refreshToken: login.tokens.refreshToken || '',
        username: result.email,
      });
      const attrs = getUserAttributesFromToken(login.tokens.idToken);
      setUser({
        id: attrs?.sub || '',
        email: attrs?.email || result.email,
        firstName: attrs?.firstName || '',
        lastName: attrs?.lastName || '',
        tenantId: attrs?.tenantId || '',
        providerType: attrs?.providerType,
        userType: attrs?.userType,
        groups: attrs?.groups || [],
      });
      // Signed in: App swaps to the signed-in routes and redirects to /dashboard, where the
      // guardian shell shows "Awaiting approval" until the agency approves.
      if (onSignedIn) onSignedIn();
      else navigate('/dashboard', { replace: true });
    } catch {
      goToLogin(result.email, 'Your account is ready. Sign in to continue.');
    }
  };

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!lastName.trim() || !dob) {
      setError('Enter the patient’s last name and date of birth.');
      return;
    }
    if (turnstileRequired && !turnstileToken) {
      setError(INVITE_MESSAGES.TURNSTILE_REQUIRED);
      return;
    }
    setBusy(true);
    const usedToken = turnstileRequired ? turnstileToken : null;
    try {
      const result = await PortalService.verifyInvite({
        code,
        lastName: lastName.trim(),
        dob,
        turnstileToken: usedToken ?? undefined,
      });
      setVerified(result);
      if (result.accountExists) {
        await register(result);
      } else {
        setStep('password');
      }
    } catch (err) {
      handleInviteError(err);
    } finally {
      setBusy(false);
      if (usedToken) {
        setTurnstileToken(null);
        setTurnstileReset((key) => key + 1);
      }
    }
  };

  const handleSetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!verified) return;
    if (!isPasswordValid(password)) {
      setError(INVITE_MESSAGES.PASSWORD_POLICY);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await register(verified, password);
    } catch (err) {
      setStep('password');
      handleInviteError(err);
    } finally {
      setBusy(false);
    }
  };

  const errorBox = error && (
    <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      {error}
    </p>
  );

  return (
    <main className="flex min-h-screen items-start justify-center bg-slate-50 px-4 py-10 sm:items-center">
      <section aria-labelledby={`${ids}-title`} className="w-full max-w-md rounded-lg bg-white p-6 shadow">
        <p className="mb-4 text-2xl font-semibold lowercase tracking-tight text-slate-800">pelora</p>
        <h1 id={`${ids}-title`} className="text-xl font-semibold text-slate-900">
          {step === 'password' ? 'Create your password' : 'Family portal invitation'}
        </h1>

        {step === 'blocked' && (
          <p role="alert" className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
            {blockedMessage}
          </p>
        )}

        {step === 'details' && (
          <form onSubmit={handleVerify} noValidate className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-slate-700">
              To confirm it’s you, enter the patient’s last name and date of birth.
            </p>
            <div>
              <label htmlFor={`${ids}-last`} className="block text-sm font-medium text-slate-800">
                Patient’s last name
              </label>
              <input
                id={`${ids}-last`}
                type="text"
                autoComplete="off"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                aria-required="true"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${ids}-dob`} className="block text-sm font-medium text-slate-800">
                Patient’s date of birth
              </label>
              <input
                id={`${ids}-dob`}
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                required
                aria-required="true"
                className={inputClass}
              />
            </div>
            {turnstileRequired && (
              <div>
                <p className="mb-2 text-sm text-slate-700">{INVITE_MESSAGES.TURNSTILE_REQUIRED}</p>
                <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileReset} />
              </div>
            )}
            {errorBox}
            <button type="submit" disabled={busy} className={buttonClass}>
              {busy && <Loader size={16} className="animate-spin" aria-hidden="true" />}
              {busy ? 'Checking...' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'password' && verified && (
          <form onSubmit={handleSetPassword} noValidate className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-slate-700">
              You’ll sign in with <span className="font-medium text-slate-900">{verified.email}</span>.
            </p>
            <div>
              <label htmlFor={`${ids}-pw`} className="block text-sm font-medium text-slate-800">
                Password
              </label>
              <input
                id={`${ids}-pw`}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                aria-required="true"
                aria-describedby={`${ids}-rules`}
                className={inputClass}
              />
            </div>
            <div id={`${ids}-rules`} className="text-sm text-slate-700">
              <p>Password must include:</p>
              <ul className="mt-1 space-y-0.5 pl-4">
                {PASSWORD_RULES.map((rule) => {
                  const met = rule.test(password);
                  return (
                    <li key={rule.id} className={met ? 'text-green-800' : 'text-slate-700'}>
                      <span aria-hidden="true">{met ? '✓' : '•'}</span> {rule.label}
                      <span className="sr-only">{met ? ' (met)' : ' (not met)'}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <label htmlFor={`${ids}-confirm`} className="block text-sm font-medium text-slate-800">
                Confirm password
              </label>
              <input
                id={`${ids}-confirm`}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                aria-required="true"
                className={inputClass}
              />
            </div>
            {errorBox}
            <button type="submit" disabled={busy} className={buttonClass}>
              {busy && <Loader size={16} className="animate-spin" aria-hidden="true" />}
              {busy ? 'Saving...' : 'Create account'}
            </button>
          </form>
        )}

        {step === 'signing-in' && (
          <div role="status" aria-live="polite" className="mt-6 flex items-center gap-2 text-slate-700">
            <Loader size={20} className="animate-spin text-teal-700" aria-hidden="true" />
            <span>Signing you in</span>
          </div>
        )}
      </section>
    </main>
  );
};

export default PortalAcceptPage;
